<script setup lang="ts">
import { RouterLink, useRoute } from 'vue-router';
import { computed, ref, watch } from 'vue';

const route = useRoute();
const navOpen = ref(false);

interface NavChild {
  to: string;
  label: string;
}
interface NavItem {
  to: string;
  label: string;
  icon: string;
  children?: NavChild[];
}

const navTop: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: '\u25A4' },
  { to: '/players', label: 'Players', icon: '\u25A0' },
  { to: '/areas', label: 'Areas', icon: '\u25A8' },
  { to: '/playlists', label: 'Playlists', icon: '\u266B' },
  {
    to: '/assets',
    label: 'Assets',
    icon: '\u25A6',
    children: [
      { to: '/assets', label: 'Assets' },
      { to: '/assets/builder', label: 'Asset builder' },
    ],
  },
];

const navBottom = [
  { to: '/labels', label: 'Labels', icon: '\u2691' },
  { to: '/licenses', label: 'Licenses', icon: '\u2692' },
  { to: '/settings', label: 'Settings', icon: '\u2699' },
] as const;

const activeName = computed(() => route.name?.toString() ?? '');

watch(
  () => route.fullPath,
  () => {
    navOpen.value = false;
  },
);
</script>

<template>
  <div class="min-h-screen grid grid-cols-1 md:grid-cols-[16rem_1fr]">
    <!-- Mobile top bar -->
    <header
      class="md:hidden sticky top-0 z-40 bg-slate-950/80 border-b border-slate-800 px-4 py-3 flex items-center gap-3"
    >
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-md p-2 text-slate-200 hover:bg-slate-800"
        aria-label="Open menu"
        @click="navOpen = true"
      >
        <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      <div class="text-emerald-400 font-bold text-lg">piCast</div>
    </header>

    <!-- Mobile backdrop -->
    <button
      v-if="navOpen"
      type="button"
      class="md:hidden fixed inset-0 z-40 bg-black/50"
      aria-label="Close menu"
      @click="navOpen = false"
    />

    <aside
      class="border-r border-slate-800 bg-slate-950/80 flex flex-col max-h-screen overflow-y-auto
        md:sticky md:top-0 md:p-4
        fixed md:static inset-y-0 left-0 z-50 w-64 p-4
      "
      :class="navOpen ? 'flex' : 'hidden md:flex'"
    >
      <div class="flex items-center mb-8 px-2">
        <div class="text-emerald-400 font-bold text-lg">piCast</div>
        <button
          type="button"
          class="md:hidden ml-auto inline-flex items-center justify-center rounded-md p-2 text-slate-200 hover:bg-slate-800"
          aria-label="Close menu"
          @click="navOpen = false"
        >
          <svg viewBox="0 0 24 24" class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <nav class="flex flex-col gap-1">
        <template v-for="item in navTop" :key="item.to">
          <RouterLink
            :to="item.to"
            class="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-slate-800/60"
            :class="
              activeName.startsWith(item.label.toLowerCase().replace(/s$/, ''))
                ? 'bg-slate-800 text-emerald-300'
                : 'text-slate-300'
            "
          >
            <span class="w-4 text-center text-slate-500">{{ item.icon }}</span>
            {{ item.label }}
          </RouterLink>
          <nav
            v-if="item.children && route.path.startsWith(item.to)"
            class="ml-7 flex flex-col gap-1 border-l border-slate-800 pl-2"
          >
            <RouterLink
              v-for="child in item.children"
              :key="child.to"
              :to="child.to"
              class="px-3 py-1.5 rounded-md text-sm hover:bg-slate-800/60"
              :class="route.path === child.to ? 'text-emerald-300' : 'text-slate-400'"
            >
              {{ child.label }}
            </RouterLink>
          </nav>
        </template>
      </nav>
      <nav class="mt-auto flex flex-col gap-1 pt-4 border-t border-slate-800">
        <RouterLink
          v-for="item in navBottom"
          :key="item.to"
          :to="item.to"
          class="flex items-center gap-3 px-3 py-2 rounded-md text-sm hover:bg-slate-800/60"
          :class="
            activeName.startsWith(item.label.toLowerCase().replace(/s$/, ''))
              ? 'bg-slate-800 text-emerald-300'
              : 'text-slate-300'
          "
        >
          <span class="w-4 text-center text-slate-500">{{ item.icon }}</span>
          {{ item.label }}
        </RouterLink>
      </nav>
      <div class="text-xs text-slate-500 px-3 pt-4 mt-4 border-t border-slate-800">v1.0.0</div>
    </aside>

    <main class="p-4 md:p-8 overflow-y-auto">
      <slot />
    </main>
  </div>
</template>
